import {
  addMonths,
  DAY,
  HOUR,
  isDateAfter,
  isDateBefore,
  MINUTE,
  SECOND,
} from '@leland-dev/leland-ui-library';
import fs from 'fs';
import groq from 'groq';
import type {
  GetStaticPaths,
  GetStaticProps,
  GetStaticPropsResult,
} from 'next';
import { useRouter } from 'next/router';
import path from 'path';
import type { ParsedUrlQuery } from 'querystring';
import { useEffect, useState } from 'react';
import unixify from 'unixify';

import { useSubscribeModal } from '../../../components/SubscribeModal';
import { SANITY_IMAGE_PROJECTION } from '../../../components/image/SanityImage';
import ArticleHero, {
  ARTICLE_HERO_CATEGORY_QUERY_SELECTION,
} from '../../../components/library/ArticleHero';
import ArticleHeroImage from '../../../components/library/ArticleHeroImage';
import BodyContent, {
  BODY_CONTENT_CONTENT_SELECTION,
} from '../../../components/library/BodyContent';
import RelatedArticles, {
  RELATED_ARTICLES_ARTICLE_PROJECTION,
} from '../../../components/library/RelatedArticles';
import Layout, {
  getStaticLayoutQuery,
  type LayoutDataProps,
} from '../../../components/page/Layout';
import { useAuth } from '../../../context/AuthContext';
import { initializeApollo } from '../../../lib/apolloClient';
import {
  ARTICLE_WRITTEN_AT_PROJECTION_FIELD,
  type ArticleWithWrittenAt,
  BASE_ARTICLES_QUERY_FILTERS,
} from '../../../lib/sanity/commonGroqs';
import {
  getSanityImageUrl,
  sanityClient,
} from '../../../lib/sanity/sanityClient';
import phog from '../../../utils/analytics/posthog/posthog';
import { HIDE_SUBSCRIBE_MODAL_KEY } from '../../../utils/constants';
import { isEventShindig } from '../../../utils/events';
import { useOnce } from '../../../utils/hooks/useOnce';
import { isLiveLelandPlusCategory } from '../../../utils/leland-plus';
import {
  getLibraryArticleUrl,
  getLibraryPageUrl,
} from '../../../utils/routing';
import { sanityCategoryIdToUrn } from '../../../utils/sanity';
import { LelandLocalStorage } from '../../../utils/storage';
import {
  generateArticleStructuredData,
  generateBreadcrumbStructuredData,
  generatePersonStructuredData,
  generateProfessionalServiceStructuredData,
} from '../../../utils/structuredData';

import {
  type ArticlePageClassFragment,
  type ArticlePageCoachBatchResponseFragment,
  type ArticlePageCoachFragment,
  ArticlePageGetCoachBySlugDocument,
  type ArticlePageGetCoachBySlugQuery,
  type ArticlePageGetCoachBySlugQueryVariables,
  ArticlePageLelandPlusContributorsDocument,
  type ArticlePageLelandPlusContributorsQuery,
  type ArticlePageLelandPlusContributorsQueryVariables,
  ArticlePageSearchClassesAndShindigsDocument,
  type ArticlePageSearchClassesAndShindigsQuery,
  type ArticlePageSearchClassesAndShindigsQueryVariables,
  type ArticlePageShindigFragment,
} from './__generated-gql-types__/[articleSlug].generated';

type Props = Partial<{
  article: ArticleWithWrittenAt;
  relatedArticles: ArticleWithWrittenAt[];
  coach?: ArticlePageCoachFragment;
  freeEvent: Possible<ArticlePageShindigFragment | ArticlePageClassFragment>;
  lelandPlusContributors: Possible<ArticlePageCoachBatchResponseFragment>;
  pageContentData: Possible<LayoutDataProps>;
}>;

const ArticlePage: NextPageWithLayout<Props> = ({
  article,
  relatedArticles,
  coach,
  freeEvent,
  lelandPlusContributors,
}) => {
  const router = useRouter();
  const [inFirst15Seconds, setInFirst15Seconds] = useState(true);
  const [didSubscribe, setDidSubscribe] = useState(
    LelandLocalStorage.getItem(HIDE_SUBSCRIBE_MODAL_KEY) !== null,
  );

  const { setOpen: setSubscribeModalOpen, Modal: SubscribeModal2 } =
    useSubscribeModal();

  const { currentUser, isLoadingUser } = useAuth();

  useOnce(
    () => {
      if (!currentUser && !isLoadingUser && !inFirst15Seconds) {
        const hideSubscribeModalLocalValue =
          LelandLocalStorage.getItem<true | number>(HIDE_SUBSCRIBE_MODAL_KEY) ??
          // check localStorage directly as fallback for those who set before abstraction^ created
          (!!localStorage.getItem(HIDE_SUBSCRIBE_MODAL_KEY) || null);

        setSubscribeModalOpen(
          hideSubscribeModalLocalValue === null ||
            (hideSubscribeModalLocalValue !== true &&
              isDateAfter(Date.now(), hideSubscribeModalLocalValue)),
        );
      }
    },
    !currentUser && !isLoadingUser && !inFirst15Seconds,
  );

  useEffect(() => {
    setTimeout(() => {
      setInFirst15Seconds(false);
    }, 15 * SECOND);
  }, []);

  const onCloseSubscribeModal = () => {
    const success = didSubscribe;
    const eventName = success
      ? 'articleSubscribeModalSubmitted'
      : 'articleSubscribeModalClosed';
    const eventData = article?.categories?.length
      ? {
          categorySlugs: article.categories.map(
            (category) => category.slug?.current,
          ),
        }
      : undefined;
    phog.capture(eventName, eventData);
    LelandLocalStorage.setItem(
      HIDE_SUBSCRIBE_MODAL_KEY,
      success ? true : Date.now() + 1 * DAY,
    );
  };

  if (!article) {
    void router.replace(getLibraryPageUrl({}));
    return null;
  }

  const {
    _id,
    title = '',
    subtitle = '',
    categories,
    writtenAt,
    featuredImage,
    content,
  } = article;

  const structuredData: Array<Record<string, unknown>> = [
    generateArticleStructuredData(article, coach),
    generateBreadcrumbStructuredData(article),
  ];

  if (coach) {
    structuredData.push(
      generatePersonStructuredData(coach),
      generateProfessionalServiceStructuredData(coach)
    );
  }

  return (
    // include key to force rerender on navigation between articles
    <article key={_id}>
      {/* Structured Data */}
      {structuredData.map((data, index) => (
        <script
          key={`structured-data-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(data).replace(/</g, '\\u003c'),
          }}
        />
      ))}
      <div className="mx-auto space-y-10 py-8 xl:max-w-7xl">
        <div className="flex flex-col items-center gap-10 lg:flex-row lg:justify-between lg:px-5 lg:py-10">
          <ArticleHero
            title={title}
            subtitle={subtitle}
            categories={categories}
            coach={coach}
            writtenAt={writtenAt}
          />

          {featuredImage ? <ArticleHeroImage image={featuredImage} /> : null}
        </div>

        {content ? (
          <div className="px-5">
            <BodyContent
              articleSlug={article.slug?.current}
              content={content}
              categories={categories}
              lelandPlusContributors={lelandPlusContributors}
              freeEvent={freeEvent}
              coach={coach}
              didSubscribe={didSubscribe}
              setDidSubscribe={setDidSubscribe}
            />
          </div>
        ) : null}
      </div>

      {relatedArticles?.length ? (
        <div className="w-full bg-leland-gray-hover">
          <div className="container-def px-8 py-16 lg:px-20">
            <RelatedArticles articles={relatedArticles} />
          </div>
        </div>
      ) : null}
      <SubscribeModal2
        categories={categories}
        didSubscribe={didSubscribe}
        setDidSubscribe={setDidSubscribe}
        onClose={onCloseSubscribeModal}
      />
    </article>
  );
};
ArticlePage.getLayout = (
  page,
  {
    article: {
      title = '',
      subtitle = '',
      slug,
      featuredImage: image,
      writtenAt,
      coachAudience,
      noIndex,
    } = {},
    pageContentData,
  },
) => (
  <Layout
    title={title}
    description={subtitle}
    type="article"
    image={
      image
        ? getSanityImageUrl({
            image,
            auto: 'format',
            width: 1200,
            height: 630,
            quality: 75,
          })
        : undefined
    }
    publishedAt={writtenAt}
    canonicalLink={getLibraryArticleUrl(slug?.current ?? '')}
    pageContentData={pageContentData}
    noIndex={!!noIndex || !!coachAudience}
  >
    {page}
  </Layout>
);

const getTimeElapsed = (startTime: number) => {
  let totalTime = Date.now() - startTime;
  const hours = Math.floor(totalTime / HOUR);
  totalTime -= hours * HOUR;
  const minutes = Math.floor(totalTime / MINUTE);
  totalTime -= minutes * MINUTE;
  const seconds = Math.floor(totalTime / SECOND);
  totalTime -= seconds * SECOND;
  const milliseconds = totalTime;

  const pad = (num: number, pad = 2) => String(num).padStart(pad, '0');

  return `${hours ? `${hours}:` : ''}${pad(minutes)}:${pad(seconds)}.${pad(
    milliseconds,
    3,
  )}`;
};

interface PathParams extends ParsedUrlQuery {
  articleSlug?: string;
}

export const getStaticPaths: GetStaticPaths<PathParams> = async () => {
  let slugs: string[];

  try {
    slugs = JSON.parse(
      fs.readFileSync(
        unixify(path.resolve(process.cwd(), `temp/slugs.json`)),
        'utf-8',
      ),
    ) as typeof slugs;
  } catch {
    const start = Date.now();
    slugs = await sanityClient.fetch<string[]>(
      groq`*[${BASE_ARTICLES_QUERY_FILTERS}][].slug.current`,
    );
    const elapsedTime = getTimeElapsed(start);
    console.log(`⛔️ (${elapsedTime}) fetched slugs:`, slugs.length);
  }

  return {
    paths: slugs.map((articleSlug) => ({ params: { articleSlug } })),
    fallback: 'blocking',
    // @TODO: is this okay, or will it enable google to take our server down?
    // revalidate: 86400,
  };
};

const ARTICLE_QUERY = groq`*[_type == "article" && lower(slug.current) == lower($articleSlug)]
  {
    _id,
    title,
    subtitle,
    categories[]->{${ARTICLE_HERO_CATEGORY_QUERY_SELECTION}},
    coach->{ slug },
    ${ARTICLE_WRITTEN_AT_PROJECTION_FIELD},
    featuredImage {${SANITY_IMAGE_PROJECTION}},
    slug,
    coachAudience,
    noIndex,
    content[] {${BODY_CONTENT_CONTENT_SELECTION}},
    "relatedArticles": *[${BASE_ARTICLES_QUERY_FILTERS} && (references(^.categories[]._ref) || references(^.goal._ref) || references(^.tagRefs[]._ref))] {
      "_score": select(references(^.categories[]._ref) => 1, 0) +
        select(references(^.goal._ref) => 1, 0) +
        select(references(^.tagRefs[]._ref) => 1, 0),
      ${RELATED_ARTICLES_ARTICLE_PROJECTION}
    } | order(_score desc) [0...12],
  }[0]
`;

const NOT_FOUND_PROPS: GetStaticPropsResult<Props> = {
  notFound: true,
};

export const getStaticProps: GetStaticProps<Props, PathParams> = async ({
  params: { articleSlug } = {},
}) => {
  if (!articleSlug) {
    return NOT_FOUND_PROPS;
  }
  let articleData: Possible<
    ArticleWithWrittenAt & { relatedArticles: ArticleWithWrittenAt[] }
  >;

  const layoutCategoriesHeaderData = await getStaticLayoutQuery();

  // prefer local cached version if it exists, fallback to cms query
  // will only exist at build time — local cached versions are not uploaded to server
  try {
    articleData = JSON.parse(
      fs.readFileSync(
        unixify(
          path.resolve(process.cwd(), `temp/articles/${articleSlug}.json`),
        ),
        'utf-8',
      ),
    ) as typeof articleData;
  } catch {
    const start = Date.now();
    articleData = await sanityClient.fetch<
      Possible<
        ArticleWithWrittenAt & { relatedArticles: ArticleWithWrittenAt[] }
      >
    >(ARTICLE_QUERY, {
      articleSlug,
    });
    const elapsedTime = getTimeElapsed(start);
    console.log(`⛔️ (${elapsedTime}) fetched article:`, articleSlug);
  }

  if (!articleData) {
    return NOT_FOUND_PROPS;
  }

  let coach: Possible<ArticlePageCoachFragment> = null;
  const apolloClient = initializeApollo();
  if (articleData.coach?.slug?.current) {
    const { data } = await apolloClient.query<
      ArticlePageGetCoachBySlugQuery,
      ArticlePageGetCoachBySlugQueryVariables
    >({
      query: ArticlePageGetCoachBySlugDocument,
      variables: { slug: articleData.coach?.slug?.current },
    });

    coach = data.coachBySlug;
  }
  const freeEvents = articleData.categories
    ? await Promise.all(
        articleData.categories.map((category) => {
          if (!category?.slug?.current) return Promise.resolve(null);
          return apolloClient.query<
            ArticlePageSearchClassesAndShindigsQuery,
            ArticlePageSearchClassesAndShindigsQueryVariables
          >({
            query: ArticlePageSearchClassesAndShindigsDocument,
            variables: {
              categoryUrn: sanityCategoryIdToUrn(category._id),
            },
          });
        }),
      )
    : null;

  const freeEvent: Possible<
    ArticlePageShindigFragment | ArticlePageClassFragment
  > =
    freeEvents?.find((freeEventData) => {
      const freeEvent =
        freeEventData?.data?.searchClassesAndShindigs?.results?.[0];
      return (
        !!freeEvent &&
        isDateBefore(
          isEventShindig(freeEvent)
            ? freeEvent.date
            : freeEvent.__typename == 'Class'
              ? (freeEvent.upcomingSessionsV2?.[0]?.startAt ?? Date.now())
              : Date.now(),
          addMonths(Date.now(), 3),
        )
      );
    })?.data?.searchClassesAndShindigs?.results?.[0] ?? null;

  const categoryIdForLelandPlusBannner = articleData.categories?.find(
    (category) => isLiveLelandPlusCategory(sanityCategoryIdToUrn(category._id)),
  )?._id;

  const contributors = categoryIdForLelandPlusBannner
    ? await apolloClient.query<
        ArticlePageLelandPlusContributorsQuery,
        ArticlePageLelandPlusContributorsQueryVariables
      >({
        query: ArticlePageLelandPlusContributorsDocument,
        variables: {
          categoryUrn: categoryIdForLelandPlusBannner,
        },
      })
    : null;

  const { relatedArticles = [], ...article } = articleData;

  return {
    props: {
      article,
      relatedArticles,
      ...(coach ? { coach } : {}),
      freeEvent,
      lelandPlusContributors:
        contributors?.data?.lelandPlusContributors ?? null,
      pageContentData: layoutCategoriesHeaderData,
    },
  };
};

export default ArticlePage;
